import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * Entity merge admin — collapse spelling variants into one product.
 *
 * Transcripts are auto-transcribed farmer phone calls, so one product arrives under
 * many spellings ("Amistar Top" also as Amistarto, Amistatok, Mistart...). Pipeline
 * grounding folds what it can decide confidently, but two products of the same brand
 * (Amistar / Amistar Top) leave some variants genuinely undecidable by rule. This is
 * where a person settles the rest.
 *
 * Counts are DISTINCT conversations, matching what the dashboard charts show — so a
 * real product (many conversations) is obvious next to a one-off misspelling (one).
 */

const S = {
  section: { marginBottom: 0 },   // sits inside CollapsibleSection, which owns the spacing
  h3: { marginBottom: '4px', fontSize: '18px', color: '#1a1a1a' },
  hint: { fontSize: '13px', color: '#6c757d', marginBottom: '16px' },
  bar: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' },
  input: { padding: '7px 10px', border: '1px solid #ced4da', borderRadius: '6px', fontSize: '13px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px', background: '#fff' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e9ecef', color: '#6c757d',
        fontSize: '11px', textTransform: 'uppercase', fontWeight: 700, whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'top' },
  num: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  btn: { padding: '5px 12px', borderRadius: '6px', border: '1px solid #ced4da', background: '#fff',
         cursor: 'pointer', fontSize: '12px', fontWeight: 600 },
  btnPrimary: { padding: '7px 16px', borderRadius: '6px', border: 'none', background: '#1a7f4b',
                color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600 },
  panel: { padding: '14px', background: '#f8f9fa', border: '1px solid #e9ecef',
           borderRadius: '8px', marginBottom: '14px' },
  warn: { padding: '10px 12px', background: '#fff4e5', border: '1px solid #ffd8a8',
          borderRadius: '6px', fontSize: '13px', marginBottom: '12px', color: '#8a5300' },
  ok: { padding: '10px 12px', background: '#e6f7ed', border: '1px solid #a3e0bd',
        borderRadius: '6px', fontSize: '13px', marginBottom: '12px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  err: { padding: '10px 12px', background: '#fdecea', border: '1px solid #f5c2c0',
         borderRadius: '6px', fontSize: '13px', marginBottom: '12px', color: '#8a1c17' },
  alias: { color: '#868e96', fontSize: '11px', display: 'block', marginTop: '2px' },
};

function EntityMerge({ org, currentUser }) {
  const [entities, setEntities] = useState([]);
  const [types, setTypes] = useState([]);
  const [type, setType] = useState('Product');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);      // { text, batch }
  const [source, setSource] = useState(null);      // entity being merged away
  const [targetType, setTargetType] = useState('Product');
  const [targetQ, setTargetQ] = useState('');
  const [targetId, setTargetId] = useState('');
  const [merges, setMerges] = useState([]);
  const [busy, setBusy] = useState(false);

  // `org` is only passed by the Dachido cross-org admin panel. From FieldHQ it is
  // omitted, and the backend scopes every query to the caller's own organisation.
  const params = useCallback((extra = {}) => (org ? { pipeline: org, ...extra } : extra), [org]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axios.get('/api/dachido/entities', { params: params({ type, q, limit: 300 }) })
      .then(res => {
        setEntities(res.data.entities || []);
        if (res.data.entity_types?.length) setTypes(res.data.entity_types);
      })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [type, q, params]);

  const loadMerges = useCallback(() => {
    axios.get('/api/dachido/entities/merges', { params: params() })
      .then(res => setMerges(res.data.merges || []))
      .catch(() => {});
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMerges(); }, [loadMerges]);

  // Candidate targets for the picker: same list endpoint, filtered to the chosen
  // type. Deliberately excludes the source itself so it can't merge into itself.
  const [targets, setTargets] = useState([]);
  useEffect(() => {
    if (!source) return;
    axios.get('/api/dachido/entities', { params: params({ type: targetType, q: targetQ, limit: 100 }) })
      .then(res => setTargets((res.data.entities || []).filter(e => e.entity_id !== source.entity_id)))
      .catch(() => setTargets([]));
  }, [source, targetType, targetQ, params]);

  const beginMerge = (e) => {
    setSource(e);
    setTargetType(e.entity_type || 'Product');
    setTargetQ('');
    setTargetId('');
    setError(null);
  };

  const doMerge = () => {
    if (!source || !targetId) return;
    setBusy(true);
    setError(null);
    axios.post('/api/dachido/entities/merge', {
      source_id: source.entity_id,
      target_id: Number(targetId),
      merged_by: currentUser?.username || currentUser?.email || 'admin',
    }, { params: params() })
      .then(res => {
        setNotice({ text: `${res.data.merged} — ${res.data.rows_moved} rows moved`,
                    batch: res.data.undo_available ? res.data.merge_batch : null });
        setSource(null);
        load();
        loadMerges();
      })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setBusy(false));
  };

  const doUndo = (batch) => {
    setBusy(true);
    setError(null);
    axios.post('/api/dachido/entities/undo', { merge_batch: batch }, { params: params() })
      .then(res => {
        setNotice({ text: `Undone — "${res.data.restored}" restored`, batch: null });
        load();
        loadMerges();
      })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setBusy(false));
  };

  const targetName = targets.find(t => String(t.entity_id) === String(targetId))?.entity_name;

  return (
    <section style={S.section}>
      <div style={S.hint}>
        Pick a misspelling, choose the real product, and merge. All its mentions move
        across, the spelling is saved so the pipeline corrects it automatically from
        now on, and the merge can be undone.
      </div>

      {error && <div style={S.err}>{error}</div>}

      {notice && (
        <div style={S.ok}>
          <span>{notice.text}</span>
          <span style={{ display: 'flex', gap: '8px' }}>
            {notice.batch && (
              <button style={S.btn} disabled={busy} onClick={() => doUndo(notice.batch)}>Undo</button>
            )}
            <button style={S.btn} onClick={() => setNotice(null)}>Dismiss</button>
          </span>
        </div>
      )}

      {/* Merge panel — appears once a source is chosen */}
      {source && (
        <div style={S.panel}>
          <div style={{ fontSize: '14px', marginBottom: '10px' }}>
            Merge <strong>{source.entity_name}</strong>{' '}
            <span style={{ color: '#6c757d' }}>
              ({source.conversations} conversation{source.conversations === 1 ? '' : 's'})
            </span>{' '}into:
          </div>
          <div style={S.bar}>
            <select style={S.input} value={targetType} onChange={e => { setTargetType(e.target.value); setTargetId(''); }}>
              {(types.length ? types : [targetType]).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input style={{ ...S.input, minWidth: '180px' }} placeholder="search target…"
                   value={targetQ} onChange={e => setTargetQ(e.target.value)} />
            <select style={{ ...S.input, minWidth: '240px' }} value={targetId}
                    onChange={e => setTargetId(e.target.value)}>
              <option value="">— choose the real product —</option>
              {targets.map(t => (
                <option key={t.entity_id} value={t.entity_id}>
                  {t.entity_name} ({t.conversations})
                </option>
              ))}
            </select>
          </div>
          {targetName && (
            <div style={S.warn}>
              <strong>{source.entity_name}</strong> will be absorbed into{' '}
              <strong>{targetName}</strong> and will no longer appear on any chart.
              This can be undone.
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...S.btnPrimary, opacity: targetId && !busy ? 1 : 0.5 }}
                    disabled={!targetId || busy} onClick={doMerge}>
              {busy ? 'Merging…' : 'Merge'}
            </button>
            <button style={S.btn} onClick={() => setSource(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Entity list */}
      <div style={S.bar}>
        <select style={S.input} value={type} onChange={e => setType(e.target.value)}>
          <option value="">All types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input style={{ ...S.input, minWidth: '220px' }} placeholder="search name or spelling…"
               value={q} onChange={e => setQ(e.target.value)} />
        <button style={S.btn} onClick={load}>Refresh</button>
        <span style={{ fontSize: '12px', color: '#6c757d' }}>
          {loading ? 'Loading…' : `${entities.length} shown`}
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Entity</th>
              <th style={S.th}>Type</th>
              <th style={{ ...S.th, ...S.num }}>Conversations</th>
              <th style={{ ...S.th, ...S.num }}>Mentions</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {entities.map(e => (
              <tr key={e.entity_id}>
                <td style={S.td}>
                  {e.entity_name}
                  {e.entity_alias && <span style={S.alias}>also: {e.entity_alias}</span>}
                </td>
                <td style={{ ...S.td, color: '#6c757d' }}>{e.entity_type}</td>
                <td style={{ ...S.td, ...S.num }}>{e.conversations}</td>
                <td style={{ ...S.td, ...S.num }}>{e.mentions}</td>
                <td style={S.td}>
                  <button style={S.btn} onClick={() => beginMerge(e)}>Merge…</button>
                </td>
              </tr>
            ))}
            {!loading && entities.length === 0 && (
              <tr><td style={{ ...S.td, color: '#6c757d' }} colSpan={5}>No entities match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Undo history */}
      {merges.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h4 style={{ fontSize: '14px', color: '#1a1a1a', marginBottom: '8px' }}>Recent merges</h4>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Merged away</th>
                <th style={S.th}>By</th>
                <th style={S.th}>When</th>
                <th style={{ ...S.th, ...S.num }}>Rows</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {merges.map(m => (
                <tr key={m.merge_batch}>
                  <td style={S.td}>{m.merged_name}</td>
                  <td style={{ ...S.td, color: '#6c757d' }}>{m.merged_by}</td>
                  <td style={{ ...S.td, color: '#6c757d' }}>{m.merged_at?.slice(0, 19).replace('T', ' ')}</td>
                  <td style={{ ...S.td, ...S.num }}>{m.rows_moved}</td>
                  <td style={S.td}>
                    <button style={S.btn} disabled={busy} onClick={() => doUndo(m.merge_batch)}>Undo</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default EntityMerge;
